import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';


// Contentful configuration
const CONTENTFUL_SPACE_ID = 'l4m1dxs9e1m1';
const CONTENTFUL_ACCESS_TOKEN = 'DdxSj8Bsf5A1t15VEaQMVy70e_n4z8fHjVBK_2oZknw';
const CONTENTFUL_API_URL = `https://cdn.contentful.com/spaces/${CONTENTFUL_SPACE_ID}/environments/master/entries`;

const Gallery = () => {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedImage, setSelectedImage] = useState(null);
    const [activeCategory, setActiveCategory] = useState('All');

    const categories = ['All', 'Spa Interiors', 'Treatments', 'Relaxation', 'Nature'];

    useEffect(() => {
        const fetchGallery = async () => {
            // Check if Contentful is configured
            if (CONTENTFUL_SPACE_ID === 'YOUR_SPACE_ID') {
                // Show demo content if not configured
                setImages(getDemoImages());
                setLoading(false);
                return;
            }

            try {
                const response = await fetch(
                    `${CONTENTFUL_API_URL}?content_type=galleryImage&order=-sys.createdAt`,
                    {
                        headers: {
                            'Authorization': `Bearer ${CONTENTFUL_ACCESS_TOKEN}`,
                        },
                    }
                );

                if (!response.ok) throw new Error('Failed to fetch gallery');

                const data = await response.json();
                const formattedImages = data.items.map((item) => ({
                    id: item.sys.id,
                    title: item.fields.title,
                    category: item.fields.category || 'Uncategorized',
                    url: item.fields.image 
                        ? `https:${data.includes?.Asset?.find(a => a.sys.id === item.fields.image.sys.id)?.fields.file.url}`
                        : null,
                    thumbnailUrl: item.fields.image 
                        ? `https:${data.includes?.Asset?.find(a => a.sys.id === item.fields.image.sys.id)?.fields.file.url}?w=400&h=400&fit=fill`
                        : null,
                }));

                setImages(formattedImages);
            } catch (err) {
                console.error('Error fetching gallery:', err);
                setImages(getDemoImages());
            } finally {
                setLoading(false);
            }
        };

        fetchGallery();
    }, []);

    // Demo images when Contentful is not configured
    const getDemoImages = () => [
        { id: '1', title: 'Luxury Spa Interior', category: 'Spa Interiors', url: 'https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=1200&q=80', thumbnailUrl: 'https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=400&q=80' },
        { id: '2', title: 'Hot Stone Therapy', category: 'Treatments', url: 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=1200&q=80', thumbnailUrl: 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=400&q=80' },
        { id: '3', title: 'Relaxation Pool', category: 'Relaxation', url: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=1200&q=80', thumbnailUrl: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&q=80' },
        { id: '4', title: 'Tropical Garden', category: 'Nature', url: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=1200&q=80', thumbnailUrl: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&q=80' },
        { id: '5', title: 'Massage Room', category: 'Spa Interiors', url: 'https://images.unsplash.com/photo-1600334129128-685c5582fd35?w=1200&q=80', thumbnailUrl: 'https://images.unsplash.com/photo-1600334129128-685c5582fd35?w=400&q=80' },
        { id: '6', title: 'Aromatherapy', category: 'Treatments', url: 'https://images.unsplash.com/photo-1519823551278-64ac92734fb1?w=1200&q=80', thumbnailUrl: 'https://images.unsplash.com/photo-1519823551278-64ac92734fb1?w=400&q=80' },
        { id: '7', title: 'Meditation Space', category: 'Relaxation', url: 'https://images.unsplash.com/photo-1591343395082-e120087004b4?w=1200&q=80', thumbnailUrl: 'https://images.unsplash.com/photo-1591343395082-e120087004b4?w=400&q=80' },
        { id: '8', title: 'Bamboo Forest', category: 'Nature', url: 'https://images.unsplash.com/photo-1515377905703-c4788e51af15?w=1200&q=80', thumbnailUrl: 'https://images.unsplash.com/photo-1515377905703-c4788e51af15?w=400&q=80' },
        { id: '9', title: 'Spa Reception', category: 'Spa Interiors', url: 'https://images.unsplash.com/photo-1560750588-73207b1ef5b8?w=1200&q=80', thumbnailUrl: 'https://images.unsplash.com/photo-1560750588-73207b1ef5b8?w=400&q=80' },
        { id: '10', title: 'Facial Treatment', category: 'Treatments', url: 'https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?w=1200&q=80', thumbnailUrl: 'https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?w=400&q=80' },
        { id: '11', title: 'Zen Garden', category: 'Nature', url: 'https://images.unsplash.com/photo-1531299244174-d247dd4e5a66?w=1200&q=80', thumbnailUrl: 'https://images.unsplash.com/photo-1531299244174-d247dd4e5a66?w=400&q=80' },
        { id: '12', title: 'Steam Room', category: 'Relaxation', url: 'https://images.unsplash.com/photo-1552693673-1bf958298935?w=1200&q=80', thumbnailUrl: 'https://images.unsplash.com/photo-1552693673-1bf958298935?w=400&q=80' },
    ];

    const filteredImages = activeCategory === 'All' 
        ? images 
        : images.filter(img => img.category === activeCategory);

    return (
        <div className="min-h-screen bg-[#F9F6F0] pt-24 noise-overlay" data-testid="gallery-page">
            <div className="container-wide mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="text-center mb-12"
                >
                    <span className="inline-block px-4 py-1.5 bg-[#722F37]/10 text-[#722F37] text-sm font-medium tracking-widest-custom uppercase rounded-full mb-4">
                        Photo Gallery
                    </span>
                    <h1 className="font-heading text-4xl sm:text-5xl text-[#4A4E4D] mb-4">
                        Visual Journey
                    </h1>
                    <p className="text-[#4A4E4D]/70 max-w-2xl mx-auto">
                        Explore the serene beauty of spa environments, treatments, and wellness spaces through our curated collection.
                    </p>
                </motion.div>

                {/* Contentful Connected */}

                {/* Category Filter */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    className="flex flex-wrap justify-center gap-3 mb-10"
                >
                    {categories.map((category) => (
                        <button
                            key={category}
                            onClick={() => setActiveCategory(category)}
                            className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
                                activeCategory === category
                                    ? 'bg-[#722F37] text-white'
                                    : 'bg-white border border-[#E0DAD0] text-[#4A4E4D] hover:border-[#722F37]'
                            }`}
                        >
                            {category}
                        </button>
                    ))}
                </motion.div>

                {loading ? (
                    <div className="flex justify-center items-center py-20">
                        <div className="w-12 h-12 border-4 border-[#722F37]/20 border-t-[#722F37] rounded-full animate-spin"></div>
                    </div>
                ) : (
                    <motion.div
                        layout
                        className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
                    >
                        <AnimatePresence>
                            {filteredImages.map((image, index) => (
                                <motion.div
                                    key={image.id}
                                    layout
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.9 }}
                                    transition={{ duration: 0.3, delay: index * 0.05 }}
                                    className="aspect-square overflow-hidden rounded-xl cursor-pointer group"
                                    onClick={() => setSelectedImage(image)}
                                >
                                    <img
                                        src={image.thumbnailUrl}
                                        alt={image.title}
                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                                        <span className="text-white font-medium text-sm">{image.title}</span>
                                    </div>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </motion.div>
                )}

                {/* Lightbox */}
                <AnimatePresence>
                    {selectedImage && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
                            onClick={() => setSelectedImage(null)}
                        >
                            <button
                                onClick={() => setSelectedImage(null)}
                                className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
                            >
                                <X className="w-6 h-6" />
                            </button>
                            <motion.img
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.9, opacity: 0 }}
                                src={selectedImage.url}
                                alt={selectedImage.title}
                                className="max-w-full max-h-[90vh] object-contain rounded-lg"
                                onClick={(e) => e.stopPropagation()}
                            />
                            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-center">
                                <p className="font-heading text-lg">{selectedImage.title}</p>
                                <p className="text-white/60 text-sm">{selectedImage.category}</p>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};

export default Gallery;
